import { useEffect, useMemo } from 'react';
import { suggestCapoForSong, type TimedChord } from '@/core/instruments/capo';
import { chordLabel } from '@/core/theory/chord';
import { soundingKeyName } from '@/core/theory/display';
import { DiagramStrip, ShapesPanel } from '@/features/chord-diagrams';
import { ChordGrid } from '@/features/chord-grid';
import { ExportMenu, PrintSheet } from '@/features/export';
import { NeckView, StageView } from '@/features/stage';
import { ChordTimeline } from '@/features/timeline';
import { TransportBar, useTransportHotkeys } from '@/features/transport';
import { KeyCapoPanel } from '@/features/transpose';
import { useImportStore } from '@/features/upload';
import { YouTubeDock } from '@/features/youtube';
import { formatTime } from '@/lib/format';
import { Cover } from '@/ui/Cover';
import { usePlayerStore } from '@/state/playerStore';
import { useScreenStore } from '@/state/screenStore';
import { useSettingsStore } from '@/state/settingsStore';
import { useDisplayOptions } from '@/state/useDisplayOptions';
import { useDisplayResult } from '@/state/useDisplayResult';
import { buttonClass } from '@/ui/button';
import styles from './PlayerPage.module.css';
import { PlayerToolbar } from './PlayerToolbar';

/** Below this, beat and key estimates have little to work with (plan section 11). */
const SHORT_CLIP_SECONDS = 10;

export function PlayerPage() {
  const songId = useScreenStore((s) => s.songId);
  const showImport = useScreenStore((s) => s.showImport);
  const player = usePlayerStore();
  const settings = useSettingsStore();
  const options = useDisplayOptions();
  const { song, message, open, close, relink } = player;
  // Without a song in the history entry (e.g. after a reload), ask for a file.
  const status = songId ? player.status : 'missing';
  const result = useDisplayResult();
  const startImport = useImportStore((s) => s.start);

  useEffect(() => {
    if (songId) void open(songId);
    return () => {
      close();
    };
  }, [songId, open, close]);

  useTransportHotkeys(status === 'ready');

  const tuning = result ? Math.round(result.tuningCents) : 0;
  const showChart = (status === 'ready' || status === 'needs-file') && result && options;
  const view = settings.playerView;
  const warnBeats = settings.changeWarningBeats;
  const instrument = player.instrument;
  const hasCapo = instrument !== 'piano' && instrument !== 'bass';
  /** YouTube songs play in their embedded video (no pitch shift there). */
  const videoId = song?.youtubeId;

  // Capo advice from this song's chords and the chosen instrument (F5.3).
  const timedChords = useMemo(() => {
    const byChord = new Map<string, TimedChord>();
    for (const seg of result?.segments ?? []) {
      if (!seg.chord) continue;
      const key = chordLabel(seg.chord);
      const entry = byChord.get(key);
      if (entry) entry.beats += seg.beatCount;
      else byChord.set(key, { chord: seg.chord, beats: seg.beatCount });
    }
    return [...byChord.values()];
  }, [result]);
  const suggestion = useMemo(
    () =>
      options && hasCapo && timedChords.length
        ? suggestCapoForSong(timedChords, { ...options, capo: 0 }, instrument)
        : undefined,
    [options, hasCapo, timedChords, instrument],
  );

  const keyCapo = options && (
    <KeyCapoPanel
      options={options}
      showCapo={hasCapo}
      suggestion={suggestion}
      onTranspose={player.setTranspose}
      onCapo={player.setCapo}
      complexity={settings.complexity}
      onComplexity={(complexity) => {
        settings.update({ complexity, easyMode: false });
      }}
      easyMode={settings.easyMode}
      onEasyMode={(easyMode) => {
        settings.update({ easyMode });
      }}
      shiftAudio={player.shiftAudio}
      onShiftAudio={videoId ? undefined : player.setShiftAudio}
      onNotation={(notation) => {
        settings.update({ notation });
      }}
      onAccidentals={(accidentals) => {
        settings.update({ accidentals });
      }}
    />
  );

  return (
    <div className={styles.layout}>
      <main className={styles.page}>
        <PlayerToolbar />

        {song && (
          <div className={styles.songBar}>
            <Cover title={song.title} src={song.cover} size={40} />
            <div className={styles.songText}>
              <h1 className={styles.title}>{song.title}</h1>
              <span className={styles.artist}>{song.artist ?? song.fileName}</span>
            </div>
            {result && options && (
              <ul className={styles.chips} aria-label="Song details">
                <li className={`${styles.chip} ${styles.chipAccent}`}>
                  Key {soundingKeyName(options)}
                </li>
                <li className={styles.chip}>{Math.round(result.beatGrid.bpm)} BPM</li>
                <li className={styles.chip}>{result.beatGrid.beatsPerBar}/4</li>
                <li className={styles.chip}>{formatTime(result.duration)}</li>
                {result.duration < SHORT_CLIP_SECONDS && (
                  <li className={styles.chip}>Short clip: chords may be less reliable</li>
                )}
                <li className={styles.chip}>
                  {tuning === 0
                    ? 'Tuned to A440'
                    : `Tuned ${tuning > 0 ? '+' : '−'}${Math.abs(tuning)}¢ from A440`}
                </li>
              </ul>
            )}
            {showChart && (
              <ExportMenu
                song={{ title: song.title, artist: song.artist }}
                result={result}
                options={options}
              />
            )}
          </div>
        )}
        {showChart && song && (
          <PrintSheet
            song={{ title: song.title, artist: song.artist }}
            result={result}
            options={options}
            instrument={instrument}
            leftHanded={settings.leftHanded}
          />
        )}

        {status === 'loading' && <p className={styles.note}>Loading…</p>}
        {status === 'missing' && (
          <div className={styles.relink}>
            <div className={styles.relinkText}>
              <h2 className={styles.relinkTitle}>Choose the file to open this song</h2>
              <p>
                Songs are kept only while this tab is open; nothing is saved, so after a reload the
                song needs its file again. The file never leaves this device.
              </p>
            </div>
            <label className={buttonClass('primary', styles.relinkButton)}>
              Choose file
              <input
                type="file"
                className={styles.hiddenInput}
                accept="audio/*,video/mp4,video/webm"
                aria-label="Choose the song's file"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  void startImport(file);
                  showImport();
                }}
              />
            </label>
          </div>
        )}
        {status === 'error' && (
          <p className={styles.note} role="alert">
            {message}
          </p>
        )}
        {status === 'needs-file' && song && (
          <div className={styles.relink}>
            <div className={styles.relinkText}>
              <h2 className={styles.relinkTitle}>Select the file to play</h2>
              <p>
                The chords of <b>{song.title}</b> are saved, but audio is never stored. Choose{' '}
                <b>{song.fileName}</b> again to play it; it never leaves this device.
              </p>
              {message && (
                <p className={styles.relinkError} role="alert">
                  {message}
                </p>
              )}
            </div>
            <label className={buttonClass('primary', styles.relinkButton)}>
              Choose file
              <input
                type="file"
                className={styles.hiddenInput}
                accept="audio/*,video/mp4,video/webm"
                aria-label={`Choose ${song.fileName}`}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (file) void relink(file);
                }}
              />
            </label>
          </div>
        )}

        {showChart && <TransportBar />}

        {showChart && (
          <div className={styles.body} data-view={view}>
            <div className={styles.main}>
              {view === 'grid' && (
                <ChordGrid
                  result={result}
                  options={options}
                  loop={player.loop}
                  textScale={settings.gridTextScale}
                  barsPerLine={settings.barsPerLine}
                  visualOffsetMs={player.visualOffsetMs}
                  onSeekBeat={player.seekToBeat}
                  warnBeats={warnBeats}
                />
              )}
              {view === 'timeline' && (
                <>
                  <DiagramStrip
                    result={result}
                    options={options}
                    instrument={instrument}
                    onInstrument={player.setInstrument}
                    leftHanded={settings.leftHanded}
                    mode={settings.diagramStrip}
                    onMode={(diagramStrip) => {
                      settings.update({ diagramStrip });
                    }}
                    visualOffsetMs={player.visualOffsetMs}
                    onSeekBeat={player.seekToBeat}
                  />
                  <ChordTimeline
                    result={result}
                    options={options}
                    visualOffsetMs={player.visualOffsetMs}
                    onSeekBeat={player.seekToBeat}
                    warnBeats={warnBeats}
                  />
                </>
              )}
              {view === 'stage' && (
                <StageView
                  result={result}
                  options={options}
                  instrument={instrument}
                  leftHanded={settings.leftHanded}
                  visualOffsetMs={player.visualOffsetMs}
                  warnBeats={warnBeats}
                />
              )}
              {view === 'neck' && (
                <NeckView
                  result={result}
                  options={options}
                  instrument={instrument}
                  leftHanded={settings.leftHanded}
                  visualOffsetMs={player.visualOffsetMs}
                />
              )}
            </div>
            {videoId ? (
              // The video is the sound source: it stays mounted whatever the view.
              <div className={styles.side}>
                <YouTubeDock videoId={videoId} duration={result.duration} />
                {view !== 'stage' && (
                  <aside className={styles.aside} aria-label="Chord settings">
                    {keyCapo}
                  </aside>
                )}
              </div>
            ) : (
              view !== 'stage' && (
                <aside className={styles.aside} aria-label="Chord settings">
                  {keyCapo}
                  {view === 'grid' && (
                    <ShapesPanel
                      result={result}
                      options={options}
                      instrument={instrument}
                      onInstrument={player.setInstrument}
                      leftHanded={settings.leftHanded}
                      onLeftHanded={(leftHanded) => {
                        settings.update({ leftHanded });
                      }}
                      visualOffsetMs={player.visualOffsetMs}
                      warnBeats={warnBeats}
                    />
                  )}
                </aside>
              )
            )}
          </div>
        )}
      </main>
    </div>
  );
}
